import { useState, useEffect, useCallback } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { useUser } from '@clerk/clerk-react';
import { useToken } from '../../hooks/useToken';
import { api } from '../../lib/api';
// using centralized api helper
import { Button } from '../ui/button';
import { useNavigate } from '@tanstack/react-router';
import { toast } from 'sonner';
import { DataNotFoundSection } from './DataNotFoundSection';
import { Bell } from 'lucide-react';
import ReactLoading from 'react-loading';

import { getEffectiveTimezone, formatUTCToZone } from '../../lib/timezone';

// Helper function to parse date strings as UTC (server times are UTC)
const parseNotificationDate = dateString => {
    if (!dateString) return null;
    // If dateString lacks timezone info, treat it as UTC by appending 'Z'
    const hasTZ = dateString.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(dateString);
    return new Date(hasTZ ? dateString : dateString + 'Z');
};

const NotificationItem = ({ notification, onClick, isLast }) => {
    const notificationDate = parseNotificationDate(notification.created_at);
    const { user } = useUser();
    const tz = getEffectiveTimezone(user);
    return (
        <div
            className={`relative py-2 text-neutral-700 cursor-pointer hover:bg-neutral-300/10 transition-colors ${
                isLast ? '' : 'border-b border-neutral-300 py-2'
            }`}
            onClick={() => onClick(notification.id)}
        >
            <h6 className="font-semibold">{notification.title}</h6>
            <p className="text-sm">{notification.message}</p>
            <div className="flex justify-between text-neutral-500 text-xs font-semibold">
                <p className="text-xs">
                    {formatUTCToZone(notificationDate, tz, { weekday: 'long', hour: 'numeric', minute: '2-digit', hour12: true })}
                </p>
                <p className="text-xs">{formatDistanceToNow(notificationDate, { addSuffix: true })}</p>
            </div>
            {!notification.is_read && <div className="bg-primary w-2 h-2 absolute top-2 right-0 rounded-full"></div>}
        </div>
    );
};

const Notifications = ({ notifications: propNotifications, loading: propLoading }) => {
    const [notifications, setNotifications] = useState(propNotifications || []);
    const [loading, setLoading] = useState(propLoading !== undefined ? propLoading : true);
    const token = useToken();
    const { user } = useUser();
    const navigate = useNavigate();

    useEffect(() => {
        setNotifications(propNotifications || []);
    }, [propNotifications]);

    useEffect(() => {
        setLoading(propLoading !== undefined ? propLoading : true);
    }, [propLoading]);

    const fetchNotifications = useCallback(async () => {
        if (!user) return;
        try {
            if (!token) return;
            const resp = await api.getUserNotifications(token);
            console.log(resp.notifications);
            setNotifications(resp.notifications);
        } catch (error) {
            console.error('Failed to fetch notifications', error);
        } finally {
            setLoading(false);
        }
    }, [token, user]);

    useEffect(() => {
        if (user && !propNotifications) {
            fetchNotifications();
        } else if (propNotifications) {
            setLoading(false);
        }
    }, [user, propNotifications, fetchNotifications]);

    const handleNotificationClick = async id => {
        const notification = notifications.find(n => n.id === id);
        if (notification?.is_read) return;

        setNotifications(prevNotifications =>
            prevNotifications.map(notif => (notif.id === id ? { ...notif, is_read: true } : notif))
        );

        try {
            if (!token) return;
            await api.markNotificationsRead(token, [id]);
        } catch (error) {
            console.error(`Failed to mark notification ${id} as read`, error);
            setNotifications(prevNotifications =>
                prevNotifications.map(notif => (notif.id === id ? { ...notif, is_read: false } : notif))
            );
            toast.error('Failed to mark notification as read.');
        }
    };

    const handleMarkAllAsRead = async () => {
        if (!user) return;
        try {
            const unreadNotificationIds = notifications.filter(n => !n.is_read).map(n => n.id);
            if (unreadNotificationIds.length === 0) {
                toast.info('No unread notifications to mark as read.');
                return;
            }
            if (!token) return;
            await api.markNotificationsRead(token, unreadNotificationIds);
            setNotifications(notifications.map(n => ({ ...n, is_read: true })));
            toast.success('All notifications marked as read.');
        } catch (error) {
            console.error('Failed to mark all notifications as read', error);
            toast.error('Failed to mark all notifications as read.');
        }
    };
    const twentyFourHoursAgo = new Date();
    twentyFourHoursAgo.setHours(twentyFourHoursAgo.getHours() - 24);

    const recentNotifications = notifications
        .filter(n => {
            const date = parseNotificationDate(n.created_at);
            return date && date > twentyFourHoursAgo;
        })
        .sort((a, b) => parseNotificationDate(b.created_at).getTime() - parseNotificationDate(a.created_at).getTime()) // Sort newest first
        .slice(0, 2); // Limit to 6 recent notifications

    const earlierNotifications = notifications
        .filter(n => {
            const date = parseNotificationDate(n.created_at);
            return date && date <= twentyFourHoursAgo;
        })
        .sort((a, b) => parseNotificationDate(b.created_at).getTime() - parseNotificationDate(a.created_at).getTime()) // Sort newest first
        .slice(0, 2); // Limit to 4 earlier notifications

    const unreadCount = notifications.filter(n => !n.is_read).length;

    if (loading) {
        return (
            <div className="px-4 py-2 flex flex-col items-center justify-center">
                <h3 className="my-2">Notifications</h3>
                <ReactLoading type="spin" color="#fe3448" height={50} width={50} />
            </div>
        );
    }

    return (
        <div className="px-4 py-2 max-h-screen overflow-y-auto">
            <div className="flex justify-between items-center">
                <h3 className="my-2 font-bold text-lg">Notifications</h3>
                {unreadCount > 0 && (
                    <Button variant="outline" size="sm" onClick={handleMarkAllAsRead}>
                        Mark all as read
                    </Button>
                )}
            </div>
            {recentNotifications.length > 0 && (
                <div className="flex flex-col">
                    <div className="flex justify-between my-1">
                        <h4>Recent</h4>
                    </div>
                    {recentNotifications.map((notif, index) => (
                        <NotificationItem
                            key={notif.id}
                            notification={notif}
                            onClick={handleNotificationClick}
                            isLast={index === recentNotifications.length - 1}
                        />
                    ))}
                </div>
            )}
            {earlierNotifications.length > 0 && (
                <div className="flex flex-col">
                    <div className="flex justify-between my-1">
                        <h4>Earlier</h4>
                    </div>
                    {earlierNotifications.map((notif, index) => (
                        <NotificationItem
                            key={notif.id}
                            notification={notif}
                            onClick={handleNotificationClick}
                            isLast={index === earlierNotifications.length - 1}
                        />
                    ))}
                </div>
            )}
            {notifications.length === 0 && (
                <div className="flex items-center justify-center">
                    <DataNotFoundSection title="No notifications yet." icon={<Bell size={30} className="text-primary MB-4" />} />
                </div>
            )}
            <Button className="w-full my-1 rounded-lg" variant="destructive" onClick={() => navigate({ to: '/notifications' })}>
                View all notifications
            </Button>
        </div>
    );
};

export default Notifications;
